<template>
  <section
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <header class="page-header">
      <h1
        id="page-title"
        class="h1"
      >
        Fehlzeiten
      </h1>
    </header>

    <div
      v-if="canViewAbsences"
      class="card"
    >
      <!-- Filterleiste -->
      <div class="filters">
        <div class="field m-0">
          <label
            for="fa-schoolyear"
            class="muted"
            style="font-size:12px;margin:0;"
          >Schuljahr</label>
          <select
            id="fa-schoolyear"
            v-model="schoolYearFilter"
            class="text-center"
            @change="applyFilters"
          >
            <option
              v-for="sy in schoolYearOptions"
              :key="`sy-${sy}`"
              :value="sy"
            >
              {{ sy }}
            </option>
          </select>
        </div>

        <div class="field m-0">
          <input
            id="fa-q"
            v-model="q"
            placeholder="Angebot, Lehrer oder Kind"
            autocomplete="off"
            @keyup="onQueryKeyup"
          >
        </div>

        <div class="field m-0">
          <label
            for="fa-status"
            class="muted"
            style="font-size:12px;margin:0;"
          >Status</label>
          <select
            id="fa-status"
            v-model="statusFilter"
            @change="applyFilters"
          >
            <option value="">
              Alle
            </option>
            <option value="entschuldigt">
              entschuldigt
            </option>
            <option value="unentschuldigt">
              unentschuldigt
            </option>
          </select>
        </div>

        <button
          class="btn btn-primary d-inline"
          :disabled="!q && !statusFilter && !schoolYearFilter"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>

      <div
        v-if="loading"
        class="muted"
        style="display:flex;align-items:center;gap:8px;"
      >
        <span
          class="spinner"
          aria-hidden="true"
        />
        Lädt ...
      </div>

      <p
        v-else-if="error"
        class="notice notice-danger"
        role="alert"
      >
        {{ error }}
      </p>

      <template v-else>
        <div
          v-if="sortedItems.length === 0"
          class="muted"
        >
          Keine Fehlzeiten vorhanden.
        </div>

        <div
          v-else
          class="absences-table"
          role="table"
          aria-label="Liste der Fehlzeiten"
        >
          <!-- Header -->
          <div
            class="absences-row header"
            role="row"
          >
            <div
              class="cell header sortable"
              role="columnheader button"
              tabindex="0"
              @click="setSort('offer_title')"
              @keydown.enter="setSort('offer_title')"
            >
              Angebot <span v-if="sortKey==='offer_title'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>

            <div
              class="cell header sortable text-center"
              role="columnheader button"
              tabindex="0"
              @click="setSort('teacher_name')"
              @keydown.enter="setSort('teacher_name')"
            >
              Lehrer <span v-if="sortKey==='teacher_name'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>

            <div
              class="cell header sortable text-center"
              role="columnheader button"
              tabindex="0"
              @click="setSort('student_name')"
              @keydown.enter="setSort('student_name')"
            >
              Kind <span v-if="sortKey==='student_name'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>

            <div
              class="cell header sortable"
              role="columnheader button"
              tabindex="0"
              @click="setSort('date')"
              @keydown.enter="setSort('date')"
            >
              Datum <span v-if="sortKey==='date'">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
            </div>

            <div
              class="cell header text-center"
              role="columnheader"
            >
              Fehlzeit
            </div>

            <div
              class="cell header text-center"
              role="columnheader"
            >
              Status
            </div>

            <div
              class="cell header text-right"
              role="columnheader"
            >
              Aktion
            </div>
          </div>

          <!-- Rows + Subrows (Accordion) -->
          <template
            v-for="(r, idx) in sortedItems"
            :key="rowKey(r, idx)"
          >
            <!-- Hauptzeile: klickbar -->
            <div
              :class="[
                'absences-row',
                r.is_day_absence ? 'day-absence' : '',
                isExpanded(rowKey(r, idx)) ? 'is-expanded' : ''
              ]"
              role="row"
              tabindex="0"
              @click="toggleRow(rowKey(r, idx))"
              @keydown.enter.prevent="toggleRow(rowKey(r, idx))"
            >
              <div
                class="cell"
                role="cell"
              >
                <strong>{{ getOfferTitle(r) }}</strong>
                <span
                  class="value d-block"
                  style="font-size: 12px; font-weight: 300; margin-top: -2px;"
                >
                  {{ r.is_day_absence ? '' : (formatTimeRange(r) || '') }}
                </span>
              </div>

              <div
                class="cell text-center"
                role="cell"
              >
                {{ r.is_day_absence ? '' : getTeacherName(r) }}
              </div>

              <div
                class="cell text-center"
                role="cell"
              >
                {{ getStudentName(r) }}
              </div>

              <div
                class="cell"
                role="cell"
              >
                {{ formatEventDate(r) }}
              </div>

              <div
                class="cell text-center"
                role="cell"
              >
                {{ formatAbsenceRange(r) }}
              </div>

              <div
                class="cell text-center"
                role="cell"
              >
                <span :class="['badge', statusBadgeClass(r)]">
                  {{ statusLabel(r) }}
                </span>
              </div>

              <div
                class="cell text-right"
                role="cell"
              >
                <button
                  v-if="getRowNotifications(r).length > 0"
                  class="icon-btn"
                  type="button"
                  title="Meldungen anzeigen"
                  aria-label="Meldungen anzeigen"
                  style="background-color: var(--primary);"
                  @click.stop="expandRow(rowKey(r, idx))"
                >
                  <i
                    class="pi pi-eye text-white"
                    aria-hidden="true"
                    style="font-size:18px;"
                  />
                </button>

                <button
                  v-else
                  class="icon-btn icon-btn--plus"
                  type="button"
                  title="Meldung erstellen"
                  aria-label="Meldung erstellen"
                  @click.stop="openCreateFromAbsence(r)"
                >
                  <i
                    class="pi pi-envelope"
                    aria-hidden="true"
                    style="font-size:16px; color:var(--muted); margin-left:-2px; margin-bottom:-2px;"
                  />
                  <svg
                    class="icon-plus-badge"
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      fill="var(--primary)"
                      stroke="color-mix(in srgb, var(--primary) 70%, black)"
                      stroke-width="1"
                    />
                    <path
                      d="M8 4.5v7M4.5 8h7"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Subrow: erst beim Klick -->
            <div
              v-if="isExpanded(rowKey(r, idx))"
              class="absences-subrow"
              role="row"
            >
              <div
                class="cell subcell"
                role="cell"
              >
                <template v-if="getRowNotifications(r).length > 0">
                  <ul class="notif-list">
                    <li
                      v-for="n in getRowNotifications(r)"
                      :key="n.id"
                      class="notif-item"
                    >
                      <div class="notif-item__main">
                        <div class="notif-line">
                          <span :class="['badge', notifStatusClass(n)]">
                            {{ notifStatusLabel(n) }}
                          </span>

                          <span class="muted">{{ notifKindLabel(n) }}</span>

                          <span class="notif-range">
                            {{ notifRangeLabel(n) }}
                          </span>
                        </div>

                        <div
                          v-if="n.subject"
                          class="notif-subject"
                        >
                          {{ n.subject }}<span
                            v-if="n.comment"
                            class="notif-comment font-light"
                            style="font-size: 12px;"
                          > ({{
                            n.comment
                          }})</span>
                        </div>

                        <div class="notif-meta muted">
                          abgegeben: <span class="font-semibold">{{ formatDateTimeBerlin(n.created_at) }}</span>
                          <span
                            v-if="n.parent_name"
                            class="muted"
                          > von <span class="font-semibold">{{
                            n.parent_name
                          }}</span></span>
                        </div>
                      </div>

                      <div class="notif-item__action">
                        <button
                          class="icon-btn"
                          type="button"
                          title="Meldung öffnen"
                          aria-label="Meldung öffnen"
                          @click.stop="openNotification(n, r)"
                        >
                          <i
                            class="pi pi-envelope"
                            aria-hidden="true"
                            style="font-size:18px;color:var(--muted);"
                          />
                        </button>
                      </div>
                    </li>
                  </ul>
                </template>

                <div
                  v-else
                  class="muted"
                >
                  Keine Eltern-Meldung zu diesem Eintrag vorhanden.
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Parent-Mobile: Cards statt Tabelle (bis 799px) -->
        <div
          v-if="sortedItems.length > 0"
          class="absences-cards"
          aria-label="Kartenansicht der Fehlzeiten"
        >
          <div
            v-for="(r, idx) in sortedItems"
            :key="`absence-card-${rowKey(r, idx)}`"
            :class="['absence-card', r.is_day_absence ? 'day-absence' : '']"
          >
            <div class="absence-card__top">
              <div class="absence-card__title">
                <strong class="absence-card__offer">{{ getOfferTitle(r) }}</strong>
                <span :class="['badge', statusBadgeClass(r)]">
                  {{ statusLabel(r) }}
                </span>
              </div>

              <div class="absence-card__meta">
                <div class="meta-row">
                  <span class="label muted">Kind</span>
                  <span class="value">{{ getStudentName(r) }}</span>
                </div>

                <div class="meta-row">
                  <span class="label muted">Lehrer</span>
                  <span class="value">{{ r.is_day_absence ? '–' : getTeacherName(r) }}</span>
                </div>

                <div class="meta-row">
                  <span class="label muted">Datum</span>
                  <span class="value">{{ formatEventDate(r) }}</span>
                </div>

                <div class="meta-row">
                  <span class="label muted">Zeitraum</span>
                  <span class="value">{{ r.is_day_absence ? '–' : (formatTimeRange(r) || '–') }}</span>
                </div>

                <div class="meta-row">
                  <span class="label muted">Fehlzeit</span>
                  <span class="value">{{ formatAbsenceRange(r) }}</span>
                </div>
              </div>
            </div>

            <details class="absence-card__details">
              <summary class="absence-card__summary position-relative">
                Eltern‑Meldungen
                <span class="muted">({{ getRowNotifications(r).length }})</span>
                <button
                  v-if="getRowNotifications(r).length === 0"
                  class="icon-btn icon-btn--plus"
                  type="button"
                  title="Meldung erstellen"
                  aria-label="Meldung erstellen"
                  style="position: absolute; top: -7px; right: -9px;"
                  @click.stop="openCreateFromAbsence(r)"
                >
                  <i
                    class="pi pi-envelope"
                    aria-hidden="true"
                    style="font-size:16px; color:var(--muted); margin-left:-2px; margin-bottom:-2px;"
                  />
                  <svg
                    class="icon-plus-badge"
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      fill="var(--primary)"
                      stroke="color-mix(in srgb, var(--primary) 70%, black)"
                      stroke-width="1"
                    />
                    <path
                      d="M8 4.5v7M4.5 8h7"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </summary>

              <div class="absence-card__details-body">
                <template v-if="getRowNotifications(r).length > 0">
                  <ul class="notif-list">
                    <li
                      v-for="n in getRowNotifications(r)"
                      :key="`card-notif-${n.id}`"
                      class="notif-item position-relative"
                    >
                      <div class="notif-line">
                        <span :class="['badge', notifStatusClass(n)]">
                          {{ notifStatusLabel(n) }}
                        </span>

                        <span class="muted">{{ notifKindLabel(n) }}</span>

                        <span class="notif-range">
                          {{ notifRangeLabel(n) }}
                        </span>
                      </div>

                      <div
                        v-if="n.subject"
                        class="notif-subject"
                      >
                        {{ n.subject }}<span
                          v-if="n.comment"
                          class="notif-comment font-light"
                          style="font-size: 12px;"
                        > ({{ n.comment }})</span>
                      </div>

                      <div class="notif-meta muted">
                        abgegeben: <span class="font-semibold">{{ formatDateTimeBerlin(n.created_at) }}</span>
                        <span
                          v-if="n.parent_name"
                          class="muted"
                        > von <span class="font-semibold">{{ n.parent_name }}</span></span>
                      </div>

                      <button
                        class="icon-btn"
                        type="button"
                        title="Meldung öffnen"
                        aria-label="Meldung öffnen"
                        style="position: absolute; right: 3px; bottom: 3px;"
                        @click.stop="openNotification(n, r)"
                      >
                        <i
                          class="pi pi-envelope"
                          aria-hidden="true"
                          style="font-size:18px;color:var(--muted);"
                        />
                      </button>
                    </li>
                  </ul>
                </template>

                <div
                  v-else
                  class="muted"
                >
                  Keine Eltern-Meldung zu diesem Eintrag vorhanden.
                </div>
              </div>
            </details>
          </div>
        </div>

        <!-- Pagination -->
        <div
          class="mt-12"
          style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
        >
          <span
            class="muted pagination-item"
            style="margin-right:auto"
          >Gesamt: {{ total }}</span>

          <label
            class="muted pagination-item"
            for="fa-limit "
          >pro Seite:</label>

          <select
            id="fa-limit"
            class="pagination-item"
            :value="limit"
            @change="changeLimit"
          >
            <option
              v-for="n in [10,20,50,100]"
              :key="n"
              :value="n"
            >
              {{ n }}
            </option>
          </select>

          <button
            class="btn"
            :disabled="page<=1"
            @click="prevPage"
          >
            Zurück
          </button>

          <span class="muted">Seite {{ page }} / {{ pages }}</span>

          <button
            class="btn"
            :disabled="page>=pages"
            @click="nextPage"
          >
            Weiter
          </button>
        </div>

        <NotificationViewDialog
          :open="notifViewOpen"
          :notification="selectedNotif"
          @close="closeNotification"
        />

        <NotificationCreateDialog
          :open="createOpen"
          :notif-parent-id="null"
          :prefill-student-id="createPrefill.studentId"
          :prefill-subject="createPrefill.subject"
          :prefill-date-from="createPrefill.dateFrom"
          :prefill-date-to="createPrefill.dateTo"
          :prefill-time-from="createPrefill.timeFrom"
          :prefill-time-to="createPrefill.timeTo"
          :prefill-kind="createPrefill.kind"
          @close="closeCreate"
          @created="onCreated"
        />
      </template>
    </div>

    <div
      v-else
      class="card"
    >
      <p
        class="notice mt-12"
        role="status"
      >
        Wartungsarbeiten: Diese Seite ist vorübergehend nicht verfügbar.
      </p>
    </div>
  </section>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue';
import api from '@/services/api';
import {useAuthStore} from '@/stores/auth';
import {schoolSettingsApi} from '@/api/schoolSettings';
import NotificationViewDialog from '@/components/notifications/NotificationViewDialog.vue';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';

const berlinTZ = 'Europe/Berlin';

const loading = ref(true);
const error = ref('');
const items = ref([]);

const auth = useAuthStore();
const currentUserId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null);

const settingsLoading = ref(true);
const parentMaintenanceEnabled = ref(false);
const parentMaintenanceExemptUserIds = ref([]);

// Dialog: Notification anzeigen
const notifViewOpen = ref(false);
const selectedNotif = ref(null);

function openNotification(n, absenceRow) {
  const fallbackStudentName =
      (absenceRow?.student_name ?? absenceRow?.studentName ?? null) ??
      (absenceRow ? getStudentName(absenceRow) : null) ??
      null;

  const studentName =
      (n?.studentName ?? n?.student_name ?? null) ??
      fallbackStudentName ??
      '—';

  selectedNotif.value = {
    ...(n || {}),
    studentName,
    student_name: studentName
  };
  notifViewOpen.value = true;
}

function closeNotification() {
  notifViewOpen.value = false;
  selectedNotif.value = null;
}

// Create-Dialog für Zeilen ohne Notifications
const createOpen = ref(false);
const createPrefill = ref({
  studentId: null,
  subject: '',
  dateFrom: null,
  dateTo: null,
  timeFrom: null,
  timeTo: null,
  kind: 'daily'
});

// Erwartet wird hier i.d.R. "HH:mm" (nicht "YYYY-MM-DD HH:mm:ss")
function extractTimeHM(v) {
  if (!v) return null;
  const s = String(v);
  const m = /(\d{2}):(\d{2})/.exec(s);
  if (m) return `${m[1]}:${m[2]}`;
  return null;
}

function openCreateFromAbsence(r) {
  const eventDay = r?.event_date ?? null;

  const isTime = !r?.is_day_absence && (r?.absence_from || r?.absence_to);

  createPrefill.value.studentId = r?.student_id ?? null;
  createPrefill.value.subject = '';
  createPrefill.value.dateFrom = eventDay;
  createPrefill.value.dateTo = eventDay;

  if (isTime) {
    console.log(r?.absence_to);
    console.log(r?.absence_from);

    createPrefill.value.kind = 'time';
    createPrefill.value.timeFrom = extractTimeHM(r?.absence_from);
    createPrefill.value.timeTo = extractTimeHM(r?.absence_to);
  } else {
    createPrefill.value.kind = 'daily';
    createPrefill.value.timeFrom = null;
    createPrefill.value.timeTo = null;
  }

  createOpen.value = true;
}

function closeCreate() {
  createOpen.value = false;
}

function onCreated() {
  // nach Erstellen neu laden, damit die Notification in der Zeile auftaucht
  createOpen.value = false;
  load();
}


const isMaintenanceExempt = computed(() => {
  // Wenn der Wartungsmodus nicht aktiviert ist, gelten alle als "befreit"
  if (!parentMaintenanceEnabled.value) return true;
  const uid = Number(currentUserId.value);
  return parentMaintenanceExemptUserIds.value.map((v) => Number(v)).includes(uid);
});

const canViewAbsences = computed(() => {
  if (!parentMaintenanceEnabled.value) return true;
  return isMaintenanceExempt.value;
});

// Expand/Collapse State (Accordion)
const expanded = ref(new Set());

const rowKey = (r, idx) => {
  return [
    r?.student_id ?? 'x',
    r?.event_date ?? 'x',
    r?.offer_title ?? 'x',
    r?.absence_from ?? 'x',
    r?.absence_to ?? 'x',
    idx
  ].join('|');
};

const isExpanded = (key) => expanded.value.has(key);

const toggleRow = (key) => {
  const s = new Set(expanded.value);
  if (s.has(key)) s.delete(key);
  else s.add(key);
  expanded.value = s;
};

const expandRow = (key) => {
  const s = new Set(expanded.value);
  s.add(key);
  expanded.value = s;
};

// Filter/Sort/Pagination
const q = ref('');
const statusFilter = ref(''); // '', 'entschuldigt', 'unentschuldigt'
const schoolYearFilter = ref(''); // '', 'YYYY/YYYY'
const schoolYearOptions = ref([]); // wird aus dem Backend (MV) befüllt
const sortKey = ref('date');
const sortDir = ref('desc');
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const pages = ref(0);
const schoolYearInitialized = ref(false); // einmalige Default-Vorauswahl

// Filtering erfolgt serverseitig
const filteredItems = computed(() => {
  return items.value || [];
});

const sortedItems = computed(() => {
  // Backend liefert bereits sortierte Daten
  return filteredItems.value;
});

// Feldablesung (Backend kann variieren)
const getOfferTitle = (r) => r?.offer_title ?? r?.offer?.title ?? '–';
const getTeacherName = (r) => r?.teacher_name ?? r?.teacher?.full_name ?? '–';
const getStudentName = (r) => r?.student_name ?? r?.student?.full_name ?? '–';

// Status helpers
const statusLabel = (r) => {
  const st = String(r?.excuse_status ?? '').toLowerCase();
  return st === 'entschuldigt' ? 'entschuldigt' : 'unentschuldigt';
};

const statusBadgeClass = (r) => {
  const st = String(r?.excuse_status ?? '').toLowerCase();
  return st === 'entschuldigt' ? 'info' : 'danger';
};

// Berlin-Zeit mit Sekunden (H:i:s)
const formatTime = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (!isNaN(d.getTime())) {
    return d.toLocaleTimeString('de-DE', {
      timeZone: berlinTZ,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }
  return String(v);
};

const formatDateTimeBerlin = (v) => {
  if (!v) return '–';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const date = d.toLocaleDateString('de-DE', {timeZone: berlinTZ, day: '2-digit', month: '2-digit', year: 'numeric'});
  const time = d.toLocaleTimeString('de-DE', {
    timeZone: berlinTZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return `${date} ${time}`;
};

function formatDateDMY(value) {
  if (!value) return null;
  const s = String(value);
  const ymd = s.length >= 10 ? s.slice(0, 10) : s;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (m) {
    const [, y, mo, d] = m;
    return `${d}.${mo}.${y}`;
  }
  const dObj = new Date(s);
  if (!isNaN(dObj.getTime())) {
    return dObj.toLocaleDateString('de-DE', {timeZone: berlinTZ, day: '2-digit', month: '2-digit', year: 'numeric'});
  }
  return null;
}

const formatTimeRange = (r) => {
  if (r?.is_day_absence) return '';
  const start = r?.offer_start_at ?? r?.start_time ?? r?.start_at;
  const end = r?.offer_end_at ?? r?.end_time ?? r?.end_at;
  const fs = formatTime(start);
  const fe = formatTime(end);
  if (fs && fe) return `${fs} – ${fe}`;
  return r?.time_range ?? '–';
};

const formatEventDate = (r) => {
  const start = r?.start_at ?? r?.offer_start_at ?? r?.start_time ?? r?.event_date;
  const end = r?.end_at ?? r?.offer_end_at ?? r?.end_time ?? r?.ends_at;
  const d1 = formatDateDMY(start);
  const d2 = formatDateDMY(end);
  if (d1 && d2 && d1 !== d2) return `${d1} -${d2}`;
  return d1 || d2 || '–';
};

const formatAbsenceRange = (r) => {
  const from = r?.absence_from ?? r?.absenceFrom;
  const to = r?.absence_to ?? r?.absenceTo;
  const ff = formatTime(from);
  const ft = formatTime(to);
  if (ff && ft) return `${ff} – ${ft}`;
  if (ff && !ft) return ff;
  return '–';
};

// Notifications pro Row (nur Eltern-Meldungen aus Backend in r.notifications)
const getRowNotifications = (r) => {
  const list = r?.notifications ?? [];
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => String(b?.created_at || '').localeCompare(String(a?.created_at || '')));
};

const notifStatusLabel = (n) => {
  const s = String(n?.status || '').toLowerCase();
  if (s === 'akzeptiert') return 'akzeptiert';
  if (s === 'abgelehnt') return 'abgelehnt';
  if (s === 'gelesen') return 'gelesen';
  return s || 'offen';
};

const notifStatusClass = (n) => {
  const s = String(n?.status || '').toLowerCase();
  if (s === 'akzeptiert') return 'info';
  if (s === 'abgelehnt') return 'danger';
  if (s === 'gelesen') return 'warn';
  return 'warn';
};

const notifKindLabel = (n) => {
  const k = String(n?.kind || '').toLowerCase();
  return k === 'time' ? 'zeitweise' : 'ganztägig';
};

const notifRangeLabel = (n) => {
  const k = String(n?.kind || '').toLowerCase();
  if (k === 'daily') {
    const d1 = formatDateDMY(n?.date_from);
    const d2 = formatDateDMY(n?.date_to);
    if (d1 && d2 && d1 !== d2) return `${d1} -${d2}`;
    return d1 || d2 || '–';
  }
  const d = formatDateDMY(n?.date_from || n?.date_to);
  const t1 = formatTime(n?.time_from);
  const t2 = formatTime(n?.time_to);
  if (d && t1 && t2) return `${d} · ${t1} – ${t2}`;
  if (d) return d;
  return '–';
};

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const {data} = await api.get('/parent/absences', {
      params: {
        page: page.value,
        limit: limit.value,
        q: q.value || undefined,
        status: statusFilter.value || undefined,
        schoolYear: schoolYearFilter.value || undefined,
        sortKey: sortKey.value,
        sortDir: sortDir.value
      }
    });
    items.value = data?.items ?? [];
    total.value = data?.total ?? 0;
    pages.value = data?.pages ?? 0;

    // Schuljahre aus Response übernehmen, absteigend sortieren und Default setzen
    if (Array.isArray(data?.schoolYears)) {
      const list = [...data.schoolYears].map(String);
      list.sort((a, b) => String(b).localeCompare(String(a)));
      schoolYearOptions.value = list;

      const prevSy = schoolYearFilter.value || '';
      if (!schoolYearInitialized.value && list.length > 0 && !schoolYearFilter.value) {
        schoolYearFilter.value = list[0]; // neuestes Schuljahr vorauswählen
      }
      if (!schoolYearInitialized.value) {
        schoolYearInitialized.value = true;
        if (prevSy === '' && schoolYearFilter.value) {
          page.value = 1;
          load(); // einmalig mit Default-Schuljahr neu laden
          return;
        }
      }
    }

    // nach Reload einklappen
    expanded.value = new Set();
  } catch (e) {
    error.value = e?.response?.data?.message || e?.response?.data?.error || 'Fehler beim Laden der Fehlzeiten.';
  } finally {
    loading.value = false;
  }
}

function setSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
  page.value = 1;
  load();
}

// Debounced Keyup für das Suchfeld
let qKeyupDebounce;

function onQueryKeyup() {
  if (qKeyupDebounce) {
    clearTimeout(qKeyupDebounce);
  }
  qKeyupDebounce = setTimeout(() => {
    applyFilters();
  }, 350);
}

function applyFilters() {
  page.value = 1;
  load();
}

function resetFilters() {
  q.value = '';
  statusFilter.value = '';
  schoolYearFilter.value = schoolYearOptions.value.length > 0 ? schoolYearOptions.value[0] : '';
  page.value = 1;
  load();
}

// Pagination
function nextPage() {
  if (page.value < pages.value) {
    page.value++;
    load();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value--;
    load();
  }
}

function changeLimit(e) {
  limit.value = Number(e.target.value);
  page.value = 1;
  load();
}

async function loadSettings() {
  settingsLoading.value = true;
  try {
    const res = await schoolSettingsApi.getCurrent();
    const data = res.data || {};
    parentMaintenanceEnabled.value = Boolean(
        data.parent_features_maintenance_enabled ?? data.parentFeaturesMaintenanceEnabled ?? false
    );
    const exempt = data.parent_features_maintenance_exempt_user_ids
        ?? data.parentFeaturesMaintenanceExemptUserIds
        ?? [];
    parentMaintenanceExemptUserIds.value = Array.isArray(exempt) ? exempt : [];
  } catch (e) {
    parentMaintenanceEnabled.value = false;
    parentMaintenanceExemptUserIds.value = [];
  } finally {
    settingsLoading.value = false;
  }
}

onMounted(async () => {
  await loadSettings();
  if (canViewAbsences.value) {
    load();
  } else {
    loading.value = false;
  }
});
</script>

<style scoped>
.absences-table {
  display: grid;
  grid-auto-rows: minmax(40px, auto);
  border-radius: 10px;
  overflow: hidden;
}

.absences-row {
  display: grid;
  grid-template-columns: auto 200px 220px 220px 150px 150px 80px;
  gap: 0;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  align-items: anchor-center;
}

/* Hauptzeilen klickbar */
.absences-row:not(.header) {
  cursor: pointer;
}

.absences-row:not(.header):hover {
  background: color-mix(in srgb, var(--surface) 92%, white);
}

.absences-row.is-expanded:not(.header) {
  border-bottom-width: 2px;
  border-bottom-color: wheat;
}

.absences-row:last-child {
  border-bottom: 0;
}

.cell {
  padding: 6px 12px;
  font-size: 14px;
  line-height: 20px;
}

.absences-row.header {
  align-items: center;
  gap: 0;
  padding: 4px 0;
  cursor: default;
}

.absences-row.header .cell {
  font-size: 13px;
  color: inherit;
  background: transparent;
  border-bottom: 0;
  font-weight: 600;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  text-decoration: underline;
}

.card {
  padding: 12px;
}

.icon-btn--plus {
  position: relative;
}

.icon-plus-badge {
  position: absolute;
  bottom: 18px;
  right: 1px;
  opacity: .8;
  transform: scale(.7);
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 16px;
  background: color-mix(in srgb, var(--info) 10%, transparent);
  color: var(--text);
}

.badge.info {
  background: color-mix(in srgb, var(--success) 40%, transparent);
}

.badge.warn {
  background: color-mix(in srgb, var(--warn) 12%, transparent);
}

.badge.danger {
  background: color-mix(in srgb, var(--danger) 20%, transparent);
}

/* Fehltag-Zeilen dezent hervorheben */
.absences-row.day-absence:not(.header) {
  background: color-mix(in srgb, var(--warn) 5%, var(--surface));
}

/* Subrow: volle Breite, eingerückt */
.absences-subrow {
  display: grid;
  grid-template-columns: 1fr;
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--surface) 92%, white);
}

.absences-subrow .subcell {
  padding: 10px 12px 12px 40px;
  font-size: 13px;
  line-height: 18px;
}

.subhead {
  margin-bottom: 6px;
}

.notif-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.notif-item {
  padding: 8px 10px;
  border-radius: 10px;
  background: whitesmoke;
  border: 1px solid color-mix(in srgb, var(--border) 80%, transparent);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
}

.notif-item__main {
  min-width: 0;
}

.notif-item__action {
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-end;
}

/* Icon-Button (wie in Notifications.vue, lokal repliziert) */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  width: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  transition: background-color 140ms, transform 80ms;
}

.icon-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--primary) 8%, transparent);
}

.icon-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.icon-btn:disabled {
  opacity: .6;
  cursor: default;
}

.notif-line {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.notif-range {
  font-weight: 500;
}

.notif-subject {
  margin-top: 6px;
  font-weight: 600;
}

.notif-comment {
  margin-top: 4px;
}

.notif-meta {
  margin-top: 6px;
  font-size: 12px;
}

.text-right {
  text-align: right;
}

/* Cards (Mobile bis 799px) */
.absences-cards {
  display: none;
}

.absence-card {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);
  display: grid;
  gap: 10px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.absence-card.day-absence {
  background: color-mix(in srgb, var(--warn) 5%, var(--surface));
}

.absence-card__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
}

.absence-card__offer {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.absence-card__meta {
  display: grid;
  gap: 2px;
}

.meta-row {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px;
  align-items: baseline;
  min-width: 0;
}

.meta-row .label {
  font-size: 12px;
}

.filters {
  display: grid;
  grid-template-columns: 120px auto 160px 90px;
  align-items: end;
  gap: 6px;
  margin-bottom: 20px;
}

.filters .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 0;
}

.filters .field select {
  height: 38px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  width: 100%;
  box-sizing: border-box;
}

.filters .field select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus);
}


.meta-row .value {
  font-size: 14px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.absence-card__details {
  border-top: 1px solid var(--border);
  padding-top: 10px;
}

.absence-card__summary {
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  line-height: 20px;
}

.absence-card__details-body {
  margin-top: 10px;
}

/* Responsiv */
@media (max-width: 799px) {
  .absences-table {
    display: none;
  }

  .page .pagination-item {
    display: none;
  }

  .filters {
    display: grid;
    grid-template-columns: auto 160px 160px 90px;
    align-items: end;
    gap: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background: whitesmoke;
    margin-bottom: 20px;
  }

  .absences-cards {
    display: grid;
    grid-template-columns:repeat(auto-fit, minmax(230px, 1fr));
    gap: 10px;
    margin-top: 12px;
  }
}
</style>